import { useState } from 'react'

import { getActiveFieldEdits } from '@fit-editor/fit-core'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import { downloadBytes } from '@/lib/download'
import {
  EXPORT_FORMAT_LABELS,
  exportActivity,
  exportFailureOutcome,
  fieldEditsBehindValidationErrors,
  type ExportFormat,
  type ExportOutcome,
} from '@/lib/export-activity'
import { selectSelectedActivity, useActivityStore } from '@/store/activity-store'
import { currentActivityAsync } from '@/worker/fit-client'

export interface ExportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const FORMATS: ExportFormat[] = ['fit', 'gpx', 'tcx', 'csv', 'geojson']

/**
 * Single-activity export (M7): pick a format, generate it (off the main
 * thread), see any warnings/self-validation result *before* downloading,
 * then download. Two explicit steps (generate, then download) rather
 * than one "export" button that both computes and downloads immediately
 * -- per the milestone brief, a FIT export's self-validation result must
 * be surfaced "before/alongside" the download, and this makes "before"
 * literal: nothing is offered for download until the user has seen the
 * generated result (warnings, and for FIT, blocked/validation errors).
 */
export function ExportDialog({ open, onOpenChange }: ExportDialogProps) {
  const loaded = useActivityStore(selectSelectedActivity)
  const [format, setFormat] = useState<ExportFormat>('fit')
  const [outcome, setOutcome] = useState<ExportOutcome | null>(null)
  const [generating, setGenerating] = useState(false)

  // Reset the generated result when the format changes or the dialog is
  // reopened -- a stale result from a previous format/open must never be
  // offered for download under the new format's label. Derived during
  // render (comparing against the previous format/open seen) rather than
  // a `useEffect`, per this codebase's established "adjust state during
  // render" pattern for exactly this kind of reset (see `CropDialog`'s
  // doc comment).
  const [prevKey, setPrevKey] = useState(`${open}:${format}`)
  const key = `${open}:${format}`
  if (key !== prevKey) {
    setPrevKey(key)
    setOutcome(null)
  }

  if (loaded == null) return null

  const handleGenerate = async () => {
    setGenerating(true)
    try {
      const { activity } = await currentActivityAsync(loaded.history)
      const result = await exportActivity(
        activity,
        format,
        loaded.fileName,
        loaded.fieldDefsByMesgNum ?? new Map(),
        loaded.history,
      )
      setOutcome(result)
    } catch (error) {
      // A rejected Worker call (as opposed to a format-level failure,
      // which `exportActivity` never throws for -- see its own doc
      // comment) is folded into the same "blocked" outcome shape so the
      // panel below shows a concrete reason instead of silently reverting
      // to "Click Generate" with no explanation.
      setOutcome(exportFailureOutcome(format, loaded.fileName, error))
    } finally {
      setGenerating(false)
    }
  }

  const handleDownload = () => {
    if (outcome == null || outcome.data == null) return
    downloadBytes(outcome.data, outcome.fileName, outcome.mimeType)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby="export-description">
        <DialogTitle>Export activity</DialogTitle>
        <DialogDescription id="export-description">
          Generates a copy of &quot;{loaded.fileName}&quot; in the chosen format, including any
          edits made so far. A FIT export is self-validated before it can be downloaded; other
          formats disclose anything they can&apos;t represent.
        </DialogDescription>

        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label htmlFor="export-format" className="text-foreground text-xs font-medium">
              Format
            </label>
            <select
              id="export-format"
              value={format}
              onChange={(e) => setFormat(e.target.value as ExportFormat)}
              className="border-input bg-background text-foreground focus-visible:ring-ring rounded-md border px-2 py-1 text-sm outline-none focus-visible:ring-2"
            >
              {FORMATS.map((f) => (
                <option key={f} value={f}>
                  {EXPORT_FORMAT_LABELS[f]}
                </option>
              ))}
            </select>
          </div>

          <ExportOutcomePanel
            outcome={outcome}
            generating={generating}
            editedFields={
              outcome?.blocked === true
                ? fieldEditsBehindValidationErrors(
                    outcome.validationErrors,
                    getActiveFieldEdits(loaded.history),
                  )
                : []
            }
          />
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={generating}
            onClick={() => void handleGenerate()}
          >
            {generating ? 'Generating…' : outcome == null ? 'Generate' : 'Regenerate'}
          </Button>
          <Button
            size="sm"
            disabled={outcome == null || outcome.data == null}
            onClick={handleDownload}
          >
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Surfaces exactly what `exportActivity` returned: for FIT, a blocked
 * self-validation failure is shown as a hard error (no download
 * possible); for every format, any `ExportWarning`s (data that doesn't
 * translate to the target format) are shown, never silently dropped --
 * this is the one non-negotiable this whole dialog exists to uphold.
 */
function ExportOutcomePanel({
  outcome,
  generating,
  editedFields,
}: {
  outcome: ExportOutcome | null
  generating: boolean
  /** Hand-edited fields (FIT data -> Edit field) the validation errors are about, e.g. "lap 2 totalDistance". */
  editedFields: string[]
}) {
  // `role="status"` on every non-alert branch (the blocked case below
  // already uses the stronger `role="alert"`) -- without it, nothing
  // announces to a screen-reader user that "Generating…" started or
  // that a result became ready after clicking Generate.
  if (generating) {
    return (
      <p role="status" className="text-muted-foreground text-xs">
        Generating…
      </p>
    )
  }
  if (outcome == null) {
    return (
      <p role="status" className="text-muted-foreground text-xs">
        Click &quot;Generate&quot; to produce the file and see any warnings before downloading.
      </p>
    )
  }

  if (outcome.blocked) {
    return (
      <div
        role="alert"
        className="border-destructive/50 bg-destructive/10 flex flex-col gap-1 rounded-md border p-2 text-xs"
      >
        <p className="text-destructive font-medium">
          Export blocked -- this file could not be produced. See details below.
        </p>
        <ul className="text-destructive list-inside list-disc">
          {outcome.validationErrors.map((error, index) => (
            <li key={index}>{error.message}</li>
          ))}
        </ul>
        {editedFields.length > 0 && (
          <p className="text-foreground mt-1">
            {editedFields.length === 1
              ? 'This is about a value you edited'
              : 'This is about values you edited'}{' '}
            by hand in FIT data: <span className="font-medium">{editedFields.join(', ')}</span>.
            Edited summary values are not recalculated, so the file would contradict its own
            records. Undo the edit (Ctrl+Z), change the value back to match the records, or remove
            the field, then generate again.
          </p>
        )}
      </div>
    )
  }

  return (
    <div
      role="status"
      className="border-border bg-background flex flex-col gap-1 rounded-md border p-2 text-xs"
    >
      <p className="text-foreground font-medium">
        Ready: {outcome.fileName}
        {outcome.format === 'fit' && ' (self-validated)'}
      </p>
      {outcome.warnings.length > 0 && (
        <div className="text-warning-text bg-warning/20 mt-1 rounded-sm p-1.5">
          <p className="font-medium">
            {outcome.warnings.length === 1 ? '1 note' : `${outcome.warnings.length} notes`}
          </p>
          <ul className="list-inside list-disc">
            {outcome.warnings.map((warning, index) => (
              <li key={index}>{warning.message}</li>
            ))}
          </ul>
        </div>
      )}
      {outcome.warnings.length === 0 && (
        <p className="text-muted-foreground">No data loss disclosed for this format.</p>
      )}
    </div>
  )
}
